// lint.ts — the shared Diagnostic → `@codemirror/lint` adapter for
// apps/cloud and libs/playground, so neither hand-rolls its own line/col →
// document-offset arithmetic (D-08, initiative Task 6).
//
// `SourceRange` is 1-based; CodeMirror wants document offsets
// (`doc.line(n).from + col`). `diagnosticOffsets` is the one function that
// does that conversion. A null `range` is the sole representation of
// unknown provenance (Task 5 leaves fragment-authored diagnostics unranged)
// — it must never be pinned to line 1, so `toLintDiagnostics` routes it to
// `unranged` instead of a squiggle; callers render that list in an error
// strip. A null `columns` means the range spans the whole line(s), which
// still renders (start-of-line to end-of-line), not an unknown.
//
// Nullable wire fields are tested with `== null`, never `=== null`: the
// payload is JSON, where a serializer that drops nulls (Pydantic's
// `exclude_none`) omits the key instead. Both spellings say "no value", and a
// strict check reads the omitted one as `undefined` and dereferences it.
//
// `doc_url` and `title` are never Diagnostic fields (D-16: derived from the
// registry by `code`, not authored per payload) — the caller passes the shipped
// `code -> {title, doc_url}` map; this module never formats or fetches a URL
// itself, and renders no title for a code the map does not carry.

import type { Text } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import { setDiagnostics, type Diagnostic as CMDiagnostic } from "@codemirror/lint";

export interface ColumnSpan {
  start_col: number;
  end_col: number;
}

export interface SourceRange {
  file: string;
  start_line: number;
  end_line: number;
  columns?: ColumnSpan | null;
}

export interface RelatedLocation {
  path: string;
  range?: SourceRange | null;
  message: string;
}

export interface DataDiagnostic {
  code: string;
  message: string;
  hint?: string | null;
  fix?: string | null;
  level: "error" | "warning";
  range?: SourceRange | null;
  related?: RelatedLocation[] | null;
}

/** Registry-derived display data for one code, resolved once per page. */
export interface DiagnosticDisplay {
  title: string;
  doc_url: string;
}

export type DiagnosticDisplayMap = Record<string, DiagnosticDisplay>;

export interface LintResult {
  diagnostics: CMDiagnostic[];
  unranged: DataDiagnostic[];
}

/**
 * Document offsets for a range, or `null` when it no longer fits the document.
 *
 * Diagnostics are computed server-side against the YAML as submitted, and the
 * round trip includes query execution — seconds during which the user keeps
 * typing. Delete lines while a render is in flight and the response comes back
 * addressing lines that no longer exist. `Text.line(n)` throws `RangeError`
 * past the end, and callers run this on the way to an auto-save, so a throw
 * here costs the user their edit. A range that outruns the document is stale
 * by definition: report it as unplaceable rather than guessing a position.
 */
function diagnosticOffsets(
  doc: Text,
  range: SourceRange,
): { from: number; to: number } | null {
  if (
    range.start_line < 1 ||
    range.end_line < range.start_line ||
    range.end_line > doc.lines
  ) {
    return null;
  }
  const startLine = doc.line(range.start_line);
  const endLine = doc.line(range.end_line);
  if (range.columns == null) {
    return { from: startLine.from, to: endLine.to };
  }
  // Columns are as stale as lines: clamp into each line so a shortened line
  // cannot push an offset past its end (or produce from > to).
  const from = Math.min(startLine.from + (range.columns.start_col - 1), startLine.to);
  const to = Math.min(endLine.from + (range.columns.end_col - 1), endLine.to);
  return { from, to: Math.max(from, to) };
}

/**
 * The flat string form, for the surfaces that cannot take a DOM node:
 * CodeMirror's own lint panel and the accessible announcement both read
 * `Diagnostic.message`. `hint` and `fix` are folded in here even though the
 * rendered tooltip hides them behind a disclosure — a screen reader must not
 * get less than the sighted reader can expand to.
 */
function diagnosticMessage(diag: DataDiagnostic): string {
  const parts = [diag.message];
  if (diag.hint) parts.push(`Hint: ${diag.hint}`);
  if (diag.fix) parts.push(`Fix: ${diag.fix}`);
  return parts.join("\n");
}

/**
 * The rendered tooltip: title and message always visible, the longer
 * remediation prose behind a `<details>` so a stack of diagnostics sharing
 * one range stays scannable. Structure only — every color, width, and font
 * lives in the consuming app's stylesheet.
 */
function diagnosticTooltip(
  diag: DataDiagnostic,
  display: DiagnosticDisplay | undefined,
): () => Node {
  return () => {
    const container = document.createElement("div");
    container.className = `cm-df-lint-message cm-df-lint-${diag.level}`;

    if (display?.title) {
      const title = document.createElement("div");
      title.className = "cm-df-lint-title";
      title.textContent = display.title;
      container.appendChild(title);
    }

    const text = document.createElement("div");
    text.className = "cm-df-lint-body";
    text.textContent = diag.message;
    container.appendChild(text);

    if (diag.hint || diag.fix) {
      const details = document.createElement("details");
      details.className = "cm-df-lint-remedy";
      const summary = document.createElement("summary");
      summary.textContent = diag.fix ? "How to fix" : "More detail";
      details.appendChild(summary);
      for (const prose of [diag.hint, diag.fix]) {
        if (!prose) continue;
        const p = document.createElement("p");
        p.textContent = prose;
        details.appendChild(p);
      }
      container.appendChild(details);
    }

    const docUrl = display?.doc_url;
    const codeEl = document.createElement(docUrl ? "a" : "span");
    codeEl.className = "cm-df-lint-code";
    codeEl.textContent = diag.code;
    if (docUrl) {
      const link = codeEl as HTMLAnchorElement;
      link.href = docUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }
    container.appendChild(codeEl);
    return container;
  };
}

/**
 * Convert dbt charts wire Diagnostic array into `@codemirror/lint`
 * diagnostics for the given document, plus the subset with no resolvable
 * range — either because the diagnostic carries none, or because the one it
 * carries no longer fits the document. `display` is the shipped
 * `code -> {title, doc_url}` map (Context: resolved once alongside the page,
 * never formatted here).
 *
 * `docFile` is the path of the file currently open in the editor
 * (project-root-relative, e.g. ``charts/sales.yml``). When non-empty, a
 * diagnostic whose `range.file` names a *different* file is routed to
 * `unranged` rather than squiggled at wrong coordinates. Pass `""` only for
 * a single-file editor that has no file path context; any app with a known
 * file path must pass it.
 */
export function toLintDiagnostics(
  doc: Text,
  diagnostics: DataDiagnostic[],
  display: DiagnosticDisplayMap,
  docFile: string,
): LintResult {
  const ranged: CMDiagnostic[] = [];
  const unranged: DataDiagnostic[] = [];
  for (const diag of diagnostics) {
    if (diag.range == null) {
      unranged.push(diag);
      continue;
    }
    if (docFile && diag.range.file && diag.range.file !== docFile) {
      unranged.push(diag);
      continue;
    }
    const offsets = diagnosticOffsets(doc, diag.range);
    if (offsets === null) {
      unranged.push(diag);
      continue;
    }
    ranged.push({
      from: offsets.from,
      to: offsets.to,
      severity: diag.level,
      message: diagnosticMessage(diag),
      renderMessage: diagnosticTooltip(diag, display[diag.code]),
    });
    // Related locations: secondary squiggles for the other half of a
    // relationship diagnostic. A related location that no longer fits the
    // document is dropped, not clamped; null range is never pinned to line 1.
    for (const rel of diag.related ?? []) {
      if (rel.range == null) continue;
      const relOffsets = diagnosticOffsets(doc, rel.range);
      if (relOffsets === null) continue;
      ranged.push({
        from: relOffsets.from,
        to: relOffsets.to,
        severity: diag.level,
        message: rel.message,
      });
    }
  }
  return { diagnostics: ranged, unranged };
}

/**
 * Push a Diagnostic array onto the editor as `@codemirror/lint` squiggles.
 * `setDiagnostics` self-enables CodeMirror's lint state field on first use —
 * no separate `linter()` extension is installed, since diagnostics arrive
 * from the app's own debounced compile round trip, not a source this module
 * would compute. Returns the unranged subset for the caller's error strip.
 *
 * `docFile` is forwarded to `toLintDiagnostics` — see its docs for the
 * cross-file filtering contract. Pass `""` only for a single-file editor with
 * no known file path; any app with a known path must pass it.
 */
export function applyDiagnostics(
  view: EditorView,
  diagnostics: DataDiagnostic[],
  display: DiagnosticDisplayMap,
  docFile: string,
): DataDiagnostic[] {
  const { diagnostics: cmDiagnostics, unranged } = toLintDiagnostics(
    view.state.doc,
    diagnostics,
    display,
    docFile,
  );
  view.dispatch(setDiagnostics(view.state, cmDiagnostics));
  return unranged;
}
